import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
  AuthRoles,
  MeteredDataSourceType,
  MeteringPointDto,
  MeteringPointMeteredDataSource,
  MeteringPointMeteredDataSourceService
} from '@app/api/dmc';
import { TableComponent } from '@shared/components/table/table.component';
import { TableConfig } from '@shared/interfaces/table/table-config';
import { MenuAction } from '@shared/interfaces/header/menu-action';
import { ModalConfig } from '@shared/interfaces/modal/modal-config';
import { HeaderService } from '@app/core/services/header.service';
import { ModalService } from '@app/core/services/modal.service';
import { StateManagerService } from '@shared/state/state-manager.service';
import { hasRole } from '@app/core/guards/has-role.guard';
import {
  meteringPointSourceTable,
  MeteringPointSourceRow
} from '@shared/table-configs/meteringpoint/meteringpoint-source.table';
import { meteringPointSourceMenuAction } from '@views/meteringpoint/meteringpoint-source/meteringpoint-source.action-menu';
import {
  meteringPointIdentificationPartial,
  meterNumberPartial,
  meterReadingOccurrencePartial
} from '@shared/columns/meteringpoint.columns';
import { meteringPointSubtypePartial } from '@shared/partials/meteringpointsubtype.partial';
import { meteringPointTypePartial } from '@shared/partials/meteringpointtype-partial.table';
import {
  AddMeterSourceModalComponent
} from '@shared/modals/meteringpoint/source/add-meter-source-modal/add-meter-source-modal.component';
import {
  AddCalculationSourceModalComponent
} from '@shared/modals/meteringpoint/source/add-calculation-source-modal/add-calculation-source-modal.component';
import {
  RemoveSourceModalComponent
} from '@shared/modals/meteringpoint/source/remove-source-modal/remove-source-modal.component';
import {
  CreateControlMeterModalComponent
} from '@shared/modals/meteringpoint/meter/create-control-meter-modal/create-control-meter-modal.component';
import {
  StopControlMeterModalComponent
} from '@shared/modals/meteringpoint/meter/stop-control-meter-modal/stop-control-meter-modal.component';

/**
 * Shows and maintains the sources the metering point's time series are produced from:
 * meter quadrants and calculation formulas on one timeline, plus verification meters.
 */
@Component({
  selector: 'app-meteringpoint-source',
  imports: [
    TableComponent
  ],
  templateUrl: './meteringpoint-source.component.html',
})
export class MeteringpointSourceComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly sourceService = inject(MeteringPointMeteredDataSourceService);
  private readonly headerService = inject(HeaderService);
  private readonly modalService = inject(ModalService);
  private readonly stateManagerService = inject(StateManagerService);
  private readonly translateService = inject(TranslateService);
  private readonly subscriptions = new Subscription();

  public id?: string;
  public meteringPoint?: MeteringPointDto;

  public readonly mpWriter = hasRole([AuthRoles.MeteringPointWriter]);

  public readonly actions: MenuAction[] = [
    {
      ...meteringPointSourceMenuAction[0],
      callback: () => this.openAddMeterModal(),
      disabled: () => !this.canChange(),
    },
    {
      ...meteringPointSourceMenuAction[1],
      callback: () => this.openAddCalculationModal(),
      disabled: () => !this.canChange(),
    },
    {
      ...meteringPointSourceMenuAction[2],
      callback: () => this.openAddControlMeterModal(),
      disabled: () => !this.canChange(),
    },
    {
      ...meteringPointSourceMenuAction[3],
      callback: () => this.openStopControlMeterModal(),
      disabled: () => !this.canChange() || this.selectedElements.length !== 1 || !this.selectedElements[0].verificationMeter,
    },
    {
      ...meteringPointSourceMenuAction[4],
      callback: () => this.openRemoveModal(),
      disabled: () => !this.canChange() || this.selectedElements.length === 0,
    },
  ];

  public readonly sourceTable: TableConfig<MeteringPointSourceRow> = {
    ...meteringPointSourceTable,
    id: 'meteringpoint-source',
    contextActions: [
      this.actions[3],
      this.actions[4],
    ],
    refreshable: true,
    refresh: () => this.fetchSources(),
  };

  public sourceData: MeteringPointSourceRow[] = [];
  public selectedElements: MeteringPointSourceRow[] = [];

  public readonly parameterTable: TableConfig<any> = {
    id: 'meteringpoint-source-parameters',
    title: 'meteringpoint.source.parameters',
    columns: [
      {
        title: 'meteringPoint.calculation.parameter.name',
        key: 'internalName'
      },
      {
        ...meteringPointIdentificationPartial,
        key: 'meteringPointIdentification',
        cellRendererParams: {
          url: '/meteringpoint',
          urlReference: 'meteringPointIdentification',
        }
      },
      {
        ...meteringPointSubtypePartial,
        key: 'meterPointSubType',
      },
      meteringPointTypePartial,
      meterReadingOccurrencePartial,
      {
        ...meterNumberPartial,
        key: 'meterIdentification',
      },
    ]
  };

  public get selectedFormula(): MeteringPointSourceRow | undefined {
    const selected = this.selectedElements.length === 1 ? this.selectedElements[0] : undefined;
    return selected?.sourceType === MeteredDataSourceType.CalculationFormula ? selected : undefined;
  }

  public ngOnInit(): void {
    this.headerService.setActions(this.actions);

    this.subscriptions.add(this.route.parent?.paramMap.subscribe((params: ParamMap) => {
      this.id = params.get('id')!;
      this.loadMeteringPoint();
      this.fetchSources();
    }));
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /** Source changes require write access; eligibility of the metering point and dates is validated by the backend. */
  public canChange(): boolean {
    return this.mpWriter;
  }

  public fetchSources(): void {
    if (!this.id) return;

    this.subscriptions.add(this.sourceService.meteringPointMeteredDataSourceIdentificationGet(this.id)
      .subscribe((sources: MeteringPointMeteredDataSource[]) => {
        this.selectedElements = [];
        this.sourceData = sources.map(source => ({
          ...source,
          sourceText: this.toSourceText(source),
          verificationMeterText: this.toVerificationMeterText(source),
        }));
      }));
  }

  public openAddMeterModal(): void {
    this.showModal({
      component: AddMeterSourceModalComponent,
      title: 'meteringpoint.source.addmeter.title',
      actions: [
        { type: 'primary', label: 'system.button.create', method: 'submit' },
        { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
      ],
      params: { meteringPoint: this.meteringPoint },
    });
  }

  public openAddCalculationModal(): void {
    this.showModal({
      component: AddCalculationSourceModalComponent,
      title: 'meteringpoint.source.addcalculation.title',
      actions: [
        { type: 'primary', label: 'system.button.create', method: 'submit' },
        { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
      ],
      params: { meteringPointIdentification: this.id },
    });
  }

  public openAddControlMeterModal(): void {
    this.showModal({
      component: CreateControlMeterModalComponent,
      title: 'meteringpoint.source.addcontrolmeter.title',
      actions: [
        { type: 'primary', label: 'system.button.create', method: 'submit' },
        { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
      ],
      params: { meteringPoint: this.meteringPoint },
    });
  }

  public openStopControlMeterModal(): void {
    this.showModal({
      component: StopControlMeterModalComponent,
      title: 'meteringpoint.source.stopcontrolmeter.title',
      actions: [
        { type: 'primary', label: 'common.stop', method: 'submit' },
        { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
      ],
      params: {
        element: this.selectedElements[0],
        meteringPointIdentification: this.id,
      },
    });
  }

  public openRemoveModal(): void {
    this.showModal({
      component: RemoveSourceModalComponent,
      title: 'meteringpoint.source.remove.title',
      actions: [
        { type: 'primary', label: 'meteringpoint.source.remove.action', method: 'submit' },
        { type: 'secondary', label: 'system.button.cancel', closeOnClick: true },
      ],
      params: {
        meteringPointIdentification: this.id,
        elements: [...this.selectedElements],
      },
    });
  }

  /** Source changes can update the metering point's master data, so both are reloaded afterwards. */
  private showModal(config: ModalConfig): void {
    this.modalService.show(config).subscribe(() => {
      this.loadMeteringPoint(true);
      this.fetchSources();
    });
  }

  private loadMeteringPoint(force = false): void {
    if (!this.id) return;

    this.subscriptions.add(this.stateManagerService.getMeteringPoint(this.id, force)
      .subscribe((meteringPoint: MeteringPointDto) => this.meteringPoint = meteringPoint));
  }

  private toVerificationMeterText(source: MeteringPointMeteredDataSource): string {
    if (source.sourceType !== MeteredDataSourceType.Meter) {
      return '';
    }

    return this.translateService.instant(source.verificationMeter ? 'common.yes' : 'common.no');
  }

  private toSourceText(source: MeteringPointMeteredDataSource): string {
    if (source.sourceType === MeteredDataSourceType.CalculationFormula) {
      return source.calculationStructureInternalName ?? source.calculationStructureDisplayName ?? '';
    }

    const meter = source.meterNumber ?? source.source ?? source.meterSerialNumber ?? '';
    const quadrant = source.quadrant ? this.translateService.instant(`enum.quadrantshort.${source.quadrant.toLowerCase()}`) : '';
    return `${meter} ${quadrant}`.trim();
  }
}
